import { ArrowUpRight, ChevronDown, X, Search, ChevronsUpDown, ArrowUpDown, Star, RefreshCw } from 'lucide-react';
import { refreshSendersAndReceivers, useContactRefresh } from '../../hooks/useApiHooks';
import { useDispatch, useSelector } from 'react-redux';
import { setShowBackToDashboardButton, setSelectedReceiver, updateWorkflowSessionState } from '../../store/reducers';
import { selectTabById, selectActiveTabData } from '../../store/selectors/tabSelectors';
import { useState, useEffect, useMemo } from 'react';
import { useLanguage, getReviewReceiverDetailsTranslation, getReviewUserDetailsTranslation, getReviewAgentsTranslation } from '../../hooks/useLanguage';
import AiIcon from '../../assets/ai-icon.png';
import { extractSuggestionsFromData } from '../../utils/suggestionUtils';

function withoutContactType(contact) {
  if (contact == null || typeof contact !== 'object') return contact;
  const { contactType: _ct, ...rest } = contact;
  return rest;
}

/** "Receiver" column UI: TARIC cliente_id — IDs resolved from `contacts` (receivers first, then senders). Stored in Redux as selectedReceiver. */
export const ReviewReceiverDetails = ({ contacts = [], navigate, tabId, declarationIndex = 0 }) => {
  const dispatch = useDispatch();
  
  // CRITICAL: Get active workflow session (source of truth for storage)
  const activeWorkflowSessionId = useSelector(state => state.workflowSessions?.activeSessionId);
  const activeWorkflowSession = useSelector(state => 
      activeWorkflowSessionId ? state.workflowSessions?.sessions?.[activeWorkflowSessionId] : null
  );
  
  // Fallback to tab-based or global for backward compatibility (UI only)
  const tabData = useSelector(state => tabId ? selectTabById(state, tabId) : selectActiveTabData(state));
  const tabSelectedReceiver = tabData ? tabData.selectedReceiver : null;

  // CRITICAL: When viewing a specific export tab, index into workflow arrays using the tab's declarationIndex.
  const effectiveDeclarationIndex = (tabData && typeof tabData.declarationIndex === 'number')
    ? tabData.declarationIndex
    : declarationIndex;
  
  // Receiver selection is stored in selectedReceiver array
  const workflowSelectedReceivers = activeWorkflowSession?.state?.selectedReceiver || [];
  const workflowSelectedReceiver = workflowSelectedReceivers[effectiveDeclarationIndex] || null;
  
  const globalSelectedReceivers = useSelector(state => state.user.selectedReceiver || []);
  const globalSelectedReceiver = globalSelectedReceivers[effectiveDeclarationIndex] || null;

  const selectedReceiverFromStores = workflowSelectedReceiver || tabSelectedReceiver || globalSelectedReceiver;
    
  // Get extracted/editable data for suggestions (from workflow session, all declarations together)
  const workflowEditableData = activeWorkflowSession?.state?.editableData || null;
  const extractedData = useSelector(state => state.documents.extractedData);
  const editableDataFromRedux = useSelector(state => state.documents.editableData);
  // Priority: workflow session > tab > global
  const editableData = workflowEditableData || (tabData ? tabData.editableData : editableDataFromRedux);

  const declarationForIndex = useMemo(() => {
    const arr = Array.isArray(editableData)
      ? editableData
      : editableData && typeof editableData === 'object'
        ? [editableData]
        : [];
    return arr[effectiveDeclarationIndex] || null;
  }, [editableData, effectiveDeclarationIndex]);

  const sendersFromStore = useSelector((state) => state.senders ?? []);
  const receiversFromStore = useSelector((state) => state.receivers ?? []);
  const contactsList = useMemo(
    () => [
      ...sendersFromStore.map((sender) => ({ ...sender, contactType: 'sender' })),
      ...receiversFromStore.map((receiver) => ({ ...receiver, contactType: 'receiver' })),
    ],
    [sendersFromStore, receiversFromStore]
  );
  const sendersOnly = useMemo(
    () => contactsList.filter((c) => c && c.contactType === 'sender'),
    [contactsList]
  );
  const receiversOnly = useMemo(
    () => contactsList.filter((c) => c && c.contactType === 'receiver'),
    [contactsList]
  );

  const clienteResolvedFromDeclarationId = useMemo(() => {
    if (selectedReceiverFromStores) return null;
    const raw = declarationForIndex?.cliente_id;
    if (raw == null || raw === '') return null;
    const num = Number(raw);
    if (!Number.isFinite(num)) return null;
    const match =
      receiversOnly.find((c) => Number(c?.id) === num) ||
      sendersOnly.find((c) => Number(c?.id) === num) ||
      null;
    return match ? withoutContactType(match) : null;
  }, [selectedReceiverFromStores, declarationForIndex, receiversOnly, sendersOnly]);

  useEffect(() => {
    if (!clienteResolvedFromDeclarationId) return;
    const existing = workflowSelectedReceivers[effectiveDeclarationIndex];
    if (existing && Number(existing.id) === Number(clienteResolvedFromDeclarationId.id)) return;

    if (activeWorkflowSessionId) {
      const next = [...(workflowSelectedReceivers || [])];
      while (next.length <= effectiveDeclarationIndex) next.push(null);
      next[effectiveDeclarationIndex] = clienteResolvedFromDeclarationId;
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { selectedReceiver: next }));
    }
    dispatch(setSelectedReceiver(clienteResolvedFromDeclarationId, effectiveDeclarationIndex));
  }, [
    clienteResolvedFromDeclarationId,
    effectiveDeclarationIndex,
    activeWorkflowSessionId,
    workflowSelectedReceivers,
    dispatch,
  ]);

  const finalSelectedReceiver = selectedReceiverFromStores || clienteResolvedFromDeclarationId;
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContactsFilter, setModalContactsFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('id-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSpanish] = useLanguage();
  const { refresh: handleRefreshContacts, isRefreshing } = useContactRefresh(refreshSendersAndReceivers);

  useEffect(() => {
    if (isModalOpen) setModalContactsFilter('all');
  }, [isModalOpen]);

  // Extract suggestions from data arrays
  const dataForSuggestions = editableData || extractedData;
  const declarationIdx = effectiveDeclarationIndex;
  const suggestions = useMemo(() => {
    return extractSuggestionsFromData(
      Array.isArray(dataForSuggestions) ? dataForSuggestions : [],
      declarationIdx
    );
  }, [dataForSuggestions, declarationIdx]);
  
  const suggestedReceivers = suggestions.clientes || [];
  

  // Match suggested receivers to receivers list only by nombre and nif (no id matching)
  const matchedSuggestedReceivers = useMemo(() => {
    const list = receiversOnly;
    return suggestedReceivers
      .map((suggestion) => {
        const matched = list.find((contact) => {
          const nombreMatch = contact.nombre && suggestion.nombre &&
            contact.nombre.toLowerCase().trim() === suggestion.nombre.toLowerCase().trim();
          const nifMatch = (contact.nif || contact.identificacion_fiscal) && suggestion.nif &&
            ((contact.nif && contact.nif.toLowerCase().trim() === suggestion.nif.toLowerCase().trim()) ||
             (contact.identificacion_fiscal && contact.identificacion_fiscal.toLowerCase().trim() === suggestion.nif.toLowerCase().trim()));

          return nombreMatch && nifMatch;
        });
        return matched ? { ...matched, score: suggestion.score, reason: suggestion.reason } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 3);
  }, [suggestedReceivers, receiversOnly]);

  const sortOptions = [
    { value: 'id-asc', label: getReviewReceiverDetailsTranslation('idAsc', isSpanish) },
    { value: 'id-desc', label: getReviewReceiverDetailsTranslation('idDesc', isSpanish) },
    { value: 'name-asc', label: getReviewReceiverDetailsTranslation('nameAZ', isSpanish) },
    { value: 'name-desc', label: getReviewReceiverDetailsTranslation('nameZA', isSpanish) },
    { value: 'date-desc', label: getReviewReceiverDetailsTranslation('newestFirst', isSpanish) },
    { value: 'date-asc', label: getReviewReceiverDetailsTranslation('oldestFirst', isSpanish) }
  ];

  const handleReceiverSelect = (raw) => {
    const receiver = withoutContactType(raw);
    if (activeWorkflowSessionId) {
      const currentReceivers = [...(workflowSelectedReceivers || [])];
      while (currentReceivers.length <= effectiveDeclarationIndex) {
        currentReceivers.push(null);
      }
      currentReceivers[effectiveDeclarationIndex] = receiver;
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
        selectedReceiver: currentReceivers,
      }));
      dispatch(setSelectedReceiver(receiver, effectiveDeclarationIndex));
    } else {
      dispatch(setSelectedReceiver(receiver, effectiveDeclarationIndex));
    }
    setIsModalOpen(false);
    setSearchQuery('');
  };

  const modalBaseContacts = useMemo(() => {
    if (modalContactsFilter === 'sender') return sendersOnly;
    if (modalContactsFilter === 'receiver') return receiversOnly;
    return contactsList;
  }, [modalContactsFilter, sendersOnly, receiversOnly, contactsList]);

  const filteredReceivers = useMemo(() => {
    const list = modalBaseContacts;
    if (!searchQuery || typeof searchQuery !== 'string') {
      return list;
    }

    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return list;
    }

    const filtered = list.filter((contact) => {
      if (!contact || typeof contact !== 'object') {
        return false;
      }
      
      // Helper function to safely convert and search a field
      const searchInField = (field) => {
        if (field == null) return false;
        if (typeof field === 'string' && field.trim() === '') return false;
        if (typeof field === 'number') {
          return String(field).toLowerCase().includes(query);
        }
        const fieldStr = String(field).toLowerCase().trim();
        return fieldStr.length > 0 && fieldStr.includes(query);
      };
      
      // Search across multiple fields
      return searchInField(contact.nombre) ||
             searchInField(contact.identificacion_fiscal) ||
             searchInField(contact.nif) ||
             searchInField(contact.eori) ||
             searchInField(contact.direccion) ||
             searchInField(contact.localidad) ||
             searchInField(contact.pais);
    });
    
    return filtered;
  }, [modalBaseContacts, searchQuery]);

  const sortedReceivers = useMemo(() => {
    const sortKey = (c) => {
      const n = Number(c?.id);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    };
    return [...filteredReceivers].sort((a, b) => {
      switch (sortBy) {
        case 'id-asc':
          return sortKey(a) - sortKey(b);
        case 'id-desc':
          return sortKey(b) - sortKey(a);
        case 'name-asc':
          return (a.nombre || '').localeCompare(b.nombre || '');
        case 'name-desc':
          return (b.nombre || '').localeCompare(a.nombre || '');
        case 'date-asc':
          return new Date(a.date_created || 0) - new Date(b.date_created || 0);
        case 'date-desc':
          return new Date(b.date_created || 0) - new Date(a.date_created || 0);
        default:
          return 0;
      }
    });
  }, [filteredReceivers, sortBy]);

  const getSortLabel = () => {
    return sortOptions.find(option => option.value === sortBy)?.label || getReviewReceiverDetailsTranslation('sortBy', isSpanish);
  };

  return (
    <div className="text-left text-sm flex flex-col">
      <div className='flex justify-between gap-4 mb-2 items-center'>
        <h1 className='font-semibold'>{getReviewReceiverDetailsTranslation('receiver', isSpanish)}</h1>
      </div>

      <div className={`border-containera form flex flex-col ${finalSelectedReceiver ? "flex-1" : ""}`}>
        <div className='relative w-full' {...(!finalSelectedReceiver && { id: `review-receiver-${declarationIndex}` })}>
          <button 
            className={`w-full flex gap-2 justify-between items-center border-container rounded-lg p-4 ${finalSelectedReceiver ? " " : ""}`} 
            onClick={() => setIsModalOpen(true)}
          >
            <div className='flex gap-2 items-center'>
              {finalSelectedReceiver && (
                <span className="text-xs bg-bgGray px-3 py-1 text-center rounded-full whitespace-nowrap h-fit">
                  {finalSelectedReceiver.id}
                </span>
              )}
              {finalSelectedReceiver ? <p className='truncate'>{finalSelectedReceiver.nombre}</p> : <p className='opacity-75 truncate'>{getReviewReceiverDetailsTranslation('selectReceiver', isSpanish)}</p>}
            </div>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
        </div>
        {finalSelectedReceiver && (
          <div className='p-4 bg-gray-100 rounded-lg mt-4 flex-1' id={`review-receiver-${declarationIndex}`}>
            <div className='review-flex flex gap-4 !mt-0'>
              <div className="form-group flex-1" id={`receiver_nombre${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="companyName">{getReviewReceiverDetailsTranslation('receiverName', isSpanish)}</label>
                <p className='font-bold'>{finalSelectedReceiver && finalSelectedReceiver.nombre || "N/A"}</p>
              </div>
              <div className="form-group flex-1" id={`receiver_nif${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
              {(finalSelectedReceiver.identificacion_fiscal && finalSelectedReceiver.identificacion_fiscal || finalSelectedReceiver.nif && finalSelectedReceiver.nif) && (
                <label htmlFor="taxId">
                  {finalSelectedReceiver.identificacion_fiscal && `${getReviewUserDetailsTranslation('identificacion_fiscal', isSpanish)}` || finalSelectedReceiver.nif && `${getReviewUserDetailsTranslation('nif', isSpanish)}`}
                </label>
              )}
              <p className='font-bold'>{finalSelectedReceiver && (finalSelectedReceiver.identificacion_fiscal && finalSelectedReceiver.identificacion_fiscal || finalSelectedReceiver.nif && finalSelectedReceiver.nif) || "N/A"}</p>
              </div>
            </div>
            <div className='review-flex flex gap-4'>
              <div className="form-group flex-1" id={`receiver_eori${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="taxId">{getReviewReceiverDetailsTranslation('receiverEori', isSpanish)}</label>
                <p className='font-bold'>{finalSelectedReceiver && finalSelectedReceiver.eori || "N/A"}</p>
              </div>
              <div className="form-group flex-1" id={`receiver_direccion${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="eori">{getReviewReceiverDetailsTranslation('receiverAddress', isSpanish)}</label>
                <p className='font-bold'>{finalSelectedReceiver && finalSelectedReceiver.direccion || "N/A"}</p>
              </div>
            </div>
            <div className='review-flex flex gap-4'>
              <div className="form-group flex-1" id={`receiver_localidad${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="city">{getReviewReceiverDetailsTranslation('receiverCity', isSpanish)}</label>
                <p className='font-bold'>{finalSelectedReceiver && finalSelectedReceiver.localidad || "N/A"}</p>
              </div>
              <div className="form-group flex-1" id={`receiver_pais${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="postalCode">{getReviewReceiverDetailsTranslation('receiverCountry', isSpanish)}</label>
                <p className='font-bold'>{finalSelectedReceiver && finalSelectedReceiver.pais || "N/A"}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Suggested Receivers */}
      {!finalSelectedReceiver && matchedSuggestedReceivers.length > 0 && (
        <>
        <h2 className="text-xs font-light mt-4">{getReviewReceiverDetailsTranslation('suggestedReceivers', isSpanish)}</h2>
        <div className="flex flex-wrap gap-2 mt-2">
          {matchedSuggestedReceivers.map((suggestedReceiver, index) => (
            <button
              key={`suggested-receiver-${declarationIndex}-${suggestedReceiver.id || index}-${index}`}
              onClick={() => handleReceiverSelect(suggestedReceiver)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full transition-colors"
              title={suggestedReceiver.reason || `Score: ${(suggestedReceiver.score * 100).toFixed(0)}%`}
            >
              <img src={AiIcon} alt="AI Icon" className="w-4 h-4" />
              <span className="font-medium text-xs truncate">{suggestedReceiver.nombre}</span>
              {suggestedReceiver.score && (
                <span className="text-[8px] text-blue-400 font-semibold ">
                  {(suggestedReceiver.score * 100).toFixed(0)}%
                </span>
              )}
            </button>
          ))}
        </div>
        </>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 animate-modalEnter">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{getReviewReceiverDetailsTranslation('selectReceiver', isSpanish)}</h2>
              <button onClick={() => {
                setIsModalOpen(false);
                setSearchQuery('');
              }} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex gap-2 mb-4 relative z-99">
              {/* Search Input */}
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border-container rounded-full"
                  placeholder={getReviewReceiverDetailsTranslation('searchReceivers', isSpanish)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <button
                type="button"
                onClick={(e) => handleRefreshContacts(e)}
                disabled={isRefreshing}
                title={getReviewAgentsTranslation('refreshList', isSpanish)}
                className="relative z-20 shrink-0 flex items-center justify-center p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
                aria-label={getReviewAgentsTranslation('refreshList', isSpanish)}
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Custom Sort Dropdown */}
              <div className="relative button-no-hover-effect">
                <button
                  className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
                  onClick={() => setIsSortOpen(!isSortOpen)}
                >
                    <ArrowUpDown className="w-4 h-4" />
                  <span>{getSortLabel()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isSortOpen && (
                  <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-999">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          sortBy === option.value ? 'text-primary' : 'text-gray-700'
                        }`}
                        onClick={() => {
                          setSortBy(option.value);
                          setIsSortOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { id: 'all', label: getReviewUserDetailsTranslation('contactsFilterAll', isSpanish) },
                { id: 'sender', label: getReviewUserDetailsTranslation('contactsFilterSenders', isSpanish) },
                { id: 'receiver', label: getReviewUserDetailsTranslation('contactsFilterReceivers', isSpanish) },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                    modalContactsFilter === id
                      ? 'bg-default text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setModalContactsFilter(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
              {contactsList.length > 0 ? (
                <div className="space-y-0">
                  {sortedReceivers.length > 0 ? (
                    sortedReceivers.map((contact, index) => (
                      <div 
                        key={`receiver-${declarationIndex}-${contact.id || index}-${index}`} 
                        className='p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray'
                        onClick={() => handleReceiverSelect(contact)}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className='flex gap-2 flex-1'>
                          <span className="text-xs bg-bgGray px-3 py-1 text-center rounded-full whitespace-nowrap h-fit min-w-[2.5rem]">
                              {contact.id}
                            </span>
                            <div className='flex flex-col flex-1 w-full'>
                            <div className='flex justify-between flex-1'>
                              <p className="font-medium flex flex-wrap items-center gap-1">
                                {contact.nombre}
                              </p>
                              {modalContactsFilter === 'all' && contact.contactType && (
                                  <span className="text-[10px] font-normal rounded-full text-gray-700">
                                    {contact.contactType === 'sender'
                                      ? getReviewUserDetailsTranslation('contactsBadgeSender', isSpanish)
                                      : getReviewUserDetailsTranslation('contactsBadgeReceiver', isSpanish)}
                                  </span>
                                )}
                            </div>
                            <div className='flex justify-between flex-1'>
                            <p className="text-sm text-gray-500">
                                {contact.pais}
                              </p>
                              {(contact.identificacion_fiscal || contact.nif) && (
                            <span className="text-[10px] text-gray-700 ">
                              {contact.identificacion_fiscal && `${getReviewUserDetailsTranslation('identificacion_fiscal', isSpanish)}: ${contact.identificacion_fiscal} ` || contact.nif && `${getReviewUserDetailsTranslation('nif', isSpanish)}: ${contact.nif} `}
                            </span>
                          )}
                            </div>
                            </div>
                          </div> 
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      {searchQuery.trim()
                        ? `${getReviewReceiverDetailsTranslation('noReceiversFound', isSpanish)} "${searchQuery}"`
                        : getReviewUserDetailsTranslation('noContactsForModalFilter', isSpanish)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className='opacity-75 mb-4'>{getReviewReceiverDetailsTranslation('noReceiversAvailable', isSpanish)}</p>
                  <div className="flex flex-wrap gap-2 items-center justify-center">
                    <button
                      type="button"
                      onClick={(e) => handleRefreshContacts(e)}
                      disabled={isRefreshing}
                      className="svg-force-white flex gap-2 items-center justify-center px-4 py-2 bg-default text-white rounded-full disabled:opacity-50"
                    >
                      <RefreshCw className={`w-5 h-5 stroke-white ${isRefreshing ? 'animate-spin' : ''}`} />
                      <span className="text-white">{getReviewAgentsTranslation('refreshList', isSpanish)}</span>
                    </button>
                    <button
                      type="button"
                      className="flex gap-2 items-center justify-center px-4 py-2 rounded-full border border-gray-300 text-gray-800 hover:bg-gray-100"
                      onClick={() => {
                        setIsModalOpen(false);
                        dispatch(setShowBackToDashboardButton(true));
                        navigate("/receiver-management");
                      }}
                    >
                      <span>{getReviewReceiverDetailsTranslation('addReceiver', isSpanish)}</span>
                      <ArrowUpRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}